
import type { Mat4 } from "./view";
import { clamp } from "../clamp";

/** The geometry of a cell grid. `CollisionMap` and `TileMap` both satisfy it,
 *  which is the point: there is one grid and both of them describe it. */
export interface CellGrid {
  readonly baseXMm: number;
  readonly baseYMm: number;
  readonly cellMm: number;
  readonly worldCellsX: number;
  readonly worldCellsY: number;
}

/** A half-open cell rectangle: `x0 <= x < x1`, `y0 <= y < y1`, in cell indices. */
export interface CellWindow {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

export interface WorldBounds {
  readonly minXMm: number;
  readonly maxXMm: number;
  readonly minYMm: number;
  readonly maxYMm: number;
}

export const EMPTY_WINDOW: CellWindow = { x0: 0, y0: 0, x1: 0, y1: 0 };

export function visibleCellWindow(
  grid: CellGrid,
  bounds: WorldBounds,
  padCells: number,
): CellWindow {
  const x0 = Math.floor((bounds.minXMm - grid.baseXMm) / grid.cellMm) - padCells;
  const x1 = Math.ceil((bounds.maxXMm - grid.baseXMm) / grid.cellMm) + padCells;
  const y0 = Math.floor((bounds.minYMm - grid.baseYMm) / grid.cellMm) - padCells;
  const y1 = Math.ceil((bounds.maxYMm - grid.baseYMm) / grid.cellMm) + padCells;
  return {
    x0: clamp(x0, 0, grid.worldCellsX),
    y0: clamp(y0, 0, grid.worldCellsY),
    // The high edge is clamped against the low one as well, so a camera that has
    // left the map entirely yields an empty window rather than an inverted one.
    x1: clamp(x1, clamp(x0, 0, grid.worldCellsX), grid.worldCellsX),
    y1: clamp(y1, clamp(y0, 0, grid.worldCellsY), grid.worldCellsY),
  };
}

/** Is `inner` entirely inside `outer`? Empty windows are contained by anything. */
export function windowContains(outer: CellWindow, inner: CellWindow): boolean {
  if (inner.x1 <= inner.x0 || inner.y1 <= inner.y0) return true;
  return (
    inner.x0 >= outer.x0 && inner.y0 >= outer.y0 && inner.x1 <= outer.x1 && inner.y1 <= outer.y1
  );
}

export function windowQuads(window: CellWindow): number {
  return Math.max(0, window.x1 - window.x0 - 1) * Math.max(0, window.y1 - window.y0 - 1);
}

/** The GPU half of a `View`: just the matrix, because that is all `worldToClip`
 *  and the vertex shader it mirrors ever multiply against. */
export interface ViewProjection {
  readonly viewProj: Mat4;
}

export function worldToClip(
  view: ViewProjection,
  xMm: number,
  yMm: number,
  zMm = 0,
): [number, number] {
  const m = view.viewProj;
  const clipX = m[0] * xMm + m[4] * yMm + m[8] * zMm + m[12];
  const clipY = m[1] * xMm + m[5] * yMm + m[9] * zMm + m[13];
  const clipW = m[3] * xMm + m[7] * yMm + m[11] * zMm + m[15];
  // A non-positive w is behind the camera; the vertex shader has no such guard
  // (a clipped vertex simply never reaches the rasteriser), but this function
  // divides explicitly and a divide by a non-positive w is exactly the mirrored,
  // wrong-side point `View.project` is written to refuse.
  if (clipW <= 0) return [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
  return [clipX / clipW, clipY / clipW];
}
